"use strict";
/**
 * WebSocket contracts — SPEC §2.1 (connectivity), §8 (reservation -> unlock
 * flow), §7 (idle media), §10 (printing).
 *
 * **Direction is a hard requirement.** Every client — the Windows agent on each
 * gaming PC, the Android TV agent, the print bridge — dials OUT to the server
 * and holds a persistent socket. The server NEVER connects inward. Venues run
 * separate staff and gaming networks, so anything requiring an inbound
 * connection is unimplementable. Reserving a PC is therefore:
 * staff app -> API -> push down the already-open socket.
 *
 * This module is the single source of truth for that wire format. Payloads are
 * zod schemas; the TypeScript types are derived from them, never hand-written.
 * The .NET and Kotlin agents mirror these shapes by hand — keep them boring:
 * no unions inside payloads except the explicit `portal.action` one, no
 * bigints (money crosses the wire as a decimal string), instants as ISO-8601.
 *
 * Objects are non-strict: unknown keys are stripped, not rejected, so a newer
 * server can add fields without breaking agents that have not auto-updated yet.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.zConfigPushMessage = exports.zAgentUpdateMessage = exports.zMediaPushMessage = exports.zDeviceLaunchMessage = exports.zDeviceWarnMessage = exports.zDevicePowerMessage = exports.zPortalReloadMessage = exports.zAppCheckUpdateMessage = exports.zSessionResumeMessage = exports.zSessionPauseMessage = exports.zDeviceLockMessage = exports.zDeviceUnlockMessage = exports.EVENTS = exports.CLIENT_EVENTS = exports.SERVER_EVENTS = exports.zPrintResultPayload = exports.zTvScanResultPayload = exports.zTvScanCandidate = exports.zTvResultPayload = exports.zPortalActionPayload = exports.zPortalActionDetail = exports.zAgentStatusPayload = exports.zAgentState = exports.zAgentHeartbeatPayload = exports.zAgentHelloPayload = exports.zAgentKind = exports.zPrintJobPayload = exports.zPrinterTarget = exports.zTvScanPayload = exports.zTvIdleStopPayload = exports.zTvIdleStartPayload = exports.zTvStatePayload = exports.zTvDesiredState = exports.zConfigPushPayload = exports.zAgentUpdatePayload = exports.zMediaPushPayload = exports.zDeviceLaunchPayload = exports.zDeviceWarnPayload = exports.zDevicePowerPayload = exports.zSessionResumePayload = exports.zPortalReloadPayload = exports.zAppCheckUpdatePayload = exports.zSessionPausePayload = exports.zDeviceLockPayload = exports.zLockReason = exports.zDeviceUnlockPayload = exports.zUnlockMode = exports.zLocale = exports.LOCALES = exports.PROTOCOL_VERSION = void 0;
exports.CLIENT_MESSAGE_SCHEMAS = exports.SERVER_MESSAGE_SCHEMAS = exports.zAnyMessage = exports.zClientMessage = exports.zServerMessage = exports.zTvScanResultMessage = exports.zTvResultMessage = exports.zPrintResultMessage = exports.zPortalActionMessage = exports.zAgentStatusMessage = exports.zAgentHeartbeatMessage = exports.zAgentHelloMessage = exports.zPrintJobMessage = exports.zTvScanMessage = exports.zTvIdleStopMessage = exports.zTvIdleStartMessage = exports.zTvStateMessage = void 0;
exports.createServerMessage = createServerMessage;
exports.createClientMessage = createClientMessage;
exports.parseServerMessage = parseServerMessage;
exports.parseClientMessage = parseClientMessage;
exports.safeParseServerMessage = safeParseServerMessage;
exports.safeParseClientMessage = safeParseClientMessage;
exports.isServerEventName = isServerEventName;
exports.isClientEventName = isClientEventName;
const zod_1 = require("zod");
/** Bumped when a breaking change lands. Sent in `agent.hello`. */
exports.PROTOCOL_VERSION = 1;
/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */
/** Entity id (uuid/cuid — kept permissive so the DB can choose). */
const zId = zod_1.z.string().min(1).max(64);
/** ISO-8601 instant with an explicit offset or `Z`. */
const zInstant = zod_1.z.iso.datetime({ offset: true });
/** Device "Tag" from the Add Device dialog (§5.2) — the agent's identity. */
const zDeviceTag = zod_1.z.string().min(1).max(64);
/** Lowercase hex SHA-256, for cached media and agent binaries. */
const zChecksum = zod_1.z.string().regex(/^[a-fA-F0-9]{64}$/, 'expected a hex sha256 digest');
const zSemver = zod_1.z.string().min(1).max(32);
/** `YYYY-MM-DD`, matching `BusinessDate` in `business-day.ts`. */
const zBusinessDate = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Money on the wire: integer millidinars as a string (JSON has no bigint). */
const zMillidinars = zod_1.z.string().regex(/^-?\d+$/, 'expected integer millidinars as a string');
exports.LOCALES = ['en', 'ar', 'ckb'];
exports.zLocale = zod_1.z.enum(exports.LOCALES);
/* -------------------------------------------------------------------------- */
/* Envelope                                                                   */
/* -------------------------------------------------------------------------- */
const envelopeShape = {
    /** Unique per message; agents echo it in acks and use it to de-duplicate. */
    id: zId,
    /** When the sender emitted it. */
    ts: zInstant,
    /** Ties a response to its request (e.g. `print.result` -> `print.job`). */
    correlationId: zId.optional(),
};
function message(event, payload) {
    return zod_1.z.object({ ...envelopeShape, event: zod_1.z.literal(event), payload });
}
/* -------------------------------------------------------------------------- */
/* Server -> client payloads                                                  */
/* -------------------------------------------------------------------------- */
/** §5.2 — `free` drops to the Windows desktop, `portal` opens the launcher. */
exports.zUnlockMode = zod_1.z.enum(['free', 'portal']);
/** §8 — reservation created: unlock this device. */
exports.zDeviceUnlockPayload = zod_1.z.object({
    deviceId: zId,
    sessionId: zId,
    mode: exports.zUnlockMode,
    /** Scheduled end; `null` for open-ended play (round-based, Boot Camp day pass). */
    endsAt: zInstant.nullable(),
    /** Tab or customer name to show on the portal chip. */
    displayName: zod_1.z.string().max(120).nullable(),
    /** Registered customer driving this session, when there is one. */
    customerId: zId.nullable(),
});
exports.zLockReason = zod_1.z.enum([
    'session_end',
    'session_void',
    'manual',
    'timeout',
    'hotkey_relock',
    'shift_close',
]);
/** §7/§8 — re-lock to the idle video. */
exports.zDeviceLockPayload = zod_1.z.object({
    deviceId: zId,
    /** `null` when no session was involved (hot-key relock, maintenance). */
    sessionId: zId.nullable(),
    reason: exports.zLockReason,
    /** Shown briefly before the idle screen, e.g. "Ask staff to extend". */
    message: zod_1.z.string().max(240).nullable(),
});
/**
 * Staff stopped the clock: seal the machine, but do NOT end the sitting.
 *
 * **Why this is not `device.lock`.** A lock means "this sitting is over" — the
 * agent forgets the session id and the scheduled end, and `agent.status` then
 * reports a machine with no session on it. A paused customer still has one,
 * still owns the tab, and is coming back to the game that is still running.
 * Reporting them as free would be a lie the whole staff board reads.
 *
 * The agent must therefore leave its lock state, its session id and the
 * processes it launched exactly as they are, and only put the portal in front
 * of whatever is playing. Resuming hands the screen back.
 */
exports.zSessionPausePayload = zod_1.z.object({
    deviceId: zId,
    sessionId: zId,
    /** Shown to the customer over the game, e.g. "Ask staff to continue". */
    message: zod_1.z.string().max(240).nullable(),
});
/**
 * "Go and look for an update NOW" — the nudge behind the staff app's
 * update-the-PCs button, instead of waiting out the half-hourly check.
 *
 * **It carries no URL, no version and no bytes, and that is the whole point.**
 * `agent.update` exists and is deliberately ignored (see the agent's README):
 * downloading and running a binary named by a socket frame is the
 * remote-code-execution path this system is built to avoid. This frame names
 * nothing. The machine goes to the server IT was configured with, over the
 * release endpoint it already polls, and verifies the checksum it always
 * verifies. Anyone who could forge this frame could, at most, make a PC do
 * early what it was going to do within the half hour anyway.
 *
 * The server only sends it to machines with NO live session: applying an update
 * restarts the portal, and a customer who paid for an hour does not lose it to
 * a release.
 */
exports.zAppCheckUpdatePayload = zod_1.z.object({
    deviceId: zId,
    /**
     * Install it once it is verified, rather than only staging it for the next
     * logon. False is the polite version — download now, swap later.
     */
    applyNow: zod_1.z.boolean(),
});
/**
 * Re-fetch the portal page on this machine. Nothing is installed, nothing
 * restarts, and the session — if the server ever sent one of these to a machine
 * that had one — would survive it only in the sense that the page would come
 * back at the sign-in screen. Which is why it never does: see below.
 *
 * WHY IT IS A SEPARATE FRAME FROM `app.check_update`. That one swaps the
 * executable and restarts the portal, so it is the heavy instrument: it needs a
 * published release to have something to do, and it is how a NEW BUILD of the
 * Windows app reaches the fleet. This one is for the far more common case — the
 * portal SCREEN changed on the server, and thirty PCs are still running the
 * JavaScript they downloaded whenever they last booted, because the portal
 * window navigates once and deliberately never reloads.
 *
 * Carries nothing but the device. The machine reloads the page it is already
 * on, from the server it is already linked to. A frame that could name a URL
 * would be the remote-code-execution path `agent.update` is refused for, with
 * a nicer name on it.
 *
 * The server only sends it to machines with NO live session. A reload drops a
 * customer to the sign-in screen mid-sitting, and no screen refresh is worth
 * that.
 */
exports.zPortalReloadPayload = zod_1.z.object({ deviceId: zId });
/** The other half: give the machine back, clock running again. */
exports.zSessionResumePayload = zod_1.z.object({
    deviceId: zId,
    sessionId: zId,
    /** Re-stamped so the agent's remaining-time snapshot is right after an extension. */
    endsAt: zInstant.nullable(),
});
/**
 * Wave 3 — staff-triggered power control of a gaming PC. The agent executes
 * the OS `shutdown` command; the confirm dialog lives staff-side and the
 * permission gate server-side — by the time this frame exists, the decision
 * is made.
 */
exports.zDevicePowerPayload = zod_1.z.object({
    deviceId: zId,
    action: zod_1.z.enum(['shutdown', 'restart']),
});
/** §5.2/§8 — "about to end" warning overlay. */
exports.zDeviceWarnPayload = zod_1.z.object({
    deviceId: zId,
    sessionId: zId,
    minutesLeft: zod_1.z.int().min(0).max(1440),
});
/**
 * §9 — start a game or app on the machine.
 *
 * The portal is a WEB PAGE running in a browser on the gaming PC, and a browser
 * cannot start `Steam.exe`. So the click travels the long way round: portal →
 * API → this event → the agent's already-open outbound socket. That keeps §2.1
 * intact (agents only ever dial out; nothing listens on a gaming PC) and, more
 * importantly, puts the server in the decision — it confirms the session is
 * really running on that device before anything is spawned.
 *
 * **Ids only, never a path.** The agent resolves `gameId` / `appId` against the
 * catalogue it already holds, preferring this device's `GameDevicePath` /
 * `AppDevicePath` override over the title default. A payload that could name an
 * executable would be a remote-code-execution path onto every PC in the venue.
 *
 * Distinct from the client→server `portal.action`, which is the agent
 * REPORTING what it launched. This is the instruction; that is the receipt.
 */
exports.zDeviceLaunchPayload = zod_1.z.discriminatedUnion('target', [
    zod_1.z.object({
        target: zod_1.z.literal('game'),
        deviceId: zId,
        sessionId: zId,
        gameId: zId,
        /** §6.5 — their own account, or a leased venue account. */
        accountChoice: zod_1.z.enum(['own', 'venue']),
        /**
         * The launcher account this machine should be signed into, or null.
         *
         * A USERNAME AND NEVER A PASSWORD. Steam remembers a session token per
         * account that has signed in on a PC (`loginusers.vdf`), and the machine
         * picks between them with `AutoLoginUser` — so the name is the whole
         * instruction. The venue's passwords stay encrypted at rest and never
         * touch a socket.
         *
         * Optional so an un-updated kiosk keeps launching exactly as it does
         * today: it will ignore the field and start the game as whoever the PC is
         * already signed in as, which is the behaviour it has now.
         */
        steamAccount: zod_1.z.string().max(64).nullish(),
        /**
         * Sign Steam OUT before doing anything, and do not launch.
         *
         * Set when the customer chose "Log in" (their own account) on a machine
         * that is currently signed into one of the VENUE's accounts. Without it
         * that button is a lie: the game simply starts on whichever account the
         * last customer's session left behind, so somebody who asked to use their
         * own account ends up playing on the venue's — with their progress and
         * saves going there too, and no sign that anything is wrong.
         *
         * No launch follows, because Steam will not start a game with nobody
         * signed in. The machine is left on the login screen for them.
         */
        steamSignOut: zod_1.z.boolean().nullish(),
    }),
    zod_1.z.object({
        target: zod_1.z.literal('app'),
        deviceId: zId,
        sessionId: zId,
        appId: zId,
    }),
]);
/** §7 — download, verify, cache and loop an idle video. */
exports.zMediaPushPayload = zod_1.z.object({
    assetId: zId,
    url: zod_1.z.url(),
    checksum: zChecksum,
    mimeType: zod_1.z.string().max(120),
    sizeBytes: zod_1.z.int().min(0),
    /** Start playing as soon as it is cached, rather than at next idle. */
    playNow: zod_1.z.boolean(),
});
/** §8 — agent auto-update. */
exports.zAgentUpdatePayload = zod_1.z.object({
    version: zSemver,
    url: zod_1.z.url(),
    checksum: zChecksum,
    /** Install immediately even if a session is running. */
    mandatory: zod_1.z.boolean(),
    notes: zod_1.z.string().max(2000).nullable(),
});
/** §6.1 — the settings an agent needs locally, pushed on connect and on change. */
exports.zConfigPushPayload = zod_1.z.object({
    /** Monotonic; the agent reports the version it holds in `agent.hello`. */
    configVersion: zod_1.z.int().min(0),
    venue: zod_1.z.object({
        venueId: zId,
        name: zod_1.z.string().max(120),
        currencySymbol: zod_1.z.string().max(8),
        locale: exports.zLocale,
        timeZone: zod_1.z.string().max(64),
        openingTime: zod_1.z.string().regex(/^\d{2}:\d{2}$/),
        closingTime: zod_1.z.string().regex(/^\d{2}:\d{2}$/),
    }),
    hotkey: zod_1.z.object({
        /** e.g. `"Control+Shift+I"`. */
        combo: zod_1.z.string().max(64),
        /** §6.1 "Hot-key Password + Enable password toggle". */
        requiresPassword: zod_1.z.boolean(),
        /** §6.1 auto-relock after configurable idle time. */
        relockAfterIdleSeconds: zod_1.z.int().min(0),
    }),
    portal: zod_1.z.object({
        /**
         * RETIRED 2026-09-24 with the "Enable Task Manager" setting (owner:
         * "remove ... Enable Task Manager"); the server always sends `false`.
         * Still REQUIRED here because every agent in the field validates this
         * frame against the schema it was built with, where the field is required
         * — dropping it would make those agents reject the whole config.push, and
         * with it the hot-key relock window it carries. Task Manager itself is
         * governed by the kiosk lockdown script (docs/kiosk-lockdown.md), not by
         * this flag.
         */
        enableTaskManager: zod_1.z.boolean(),
        enableAccountCreation: zod_1.z.boolean(),
        enableOrderFromPortal: zod_1.z.boolean(),
        paymentMethods: zod_1.z.array(zod_1.z.string().max(64)),
        /** §6.5 "Platform accounts in the portal", e.g. Random. */
        platformAccountStrategy: zod_1.z.enum(['random', 'fixed']),
        enableLoginWithCard: zod_1.z.boolean(),
        loginHoldTimeSeconds: zod_1.z.int().min(0),
    }),
    session: zod_1.z.object({
        /** §5.2 "about to end" threshold, e.g. 10. */
        aboutToEndWarningMinutes: zod_1.z.int().min(0),
    }),
    /**
     * §6.1 "Central Device IP" (2026-09-24) — the LAN base URL of the venue's
     * MAIN-PC bridge media server, `http://<Central Device IP>:8478` (8478 is
     * the bridge's LAN media port — the one it already serves the PS5 TVs on,
     * `tv.mediaPort`), which a PC should try FIRST for idle videos before
     * falling back to the hosted URL in `media.push`. Null or absent = no LAN
     * source (none set, or not an IPv4 address); download from the server.
     *
     * OPTIONAL, and it must stay so: agents already deployed validate this frame
     * with a schema that has no such key. Zod objects strip unknown keys, so an
     * old agent simply never sees it — but making it required here would make
     * every NEW agent reject a config.push from a server that predates it.
     */
    lanMediaBaseUrl: zod_1.z.string().max(300).nullish(),
});
/**
 * §7 — the desired state of every venue TV the BRIDGE drives (owner,
 * 2026-08-20: Android TVs with static IPs in front of the consoles).
 *
 * This is the bridge-driven variant of TV control: no software runs on the
 * TV. The venue's bridge PC (the same agent that prints) receives the whole
 * fleet's desired state and makes it so over the LAN — Google Cast for the
 * idle video (casting takes the screen; stopping it steps aside), ADB key
 * events for the deterministic HDMI switch to the console. `tv.idle_start` /
 * `tv.idle_stop` below remain the contract for a future ON-TV agent; this
 * frame is addressed to the `print-bridge` kind room instead.
 *
 * ALWAYS the full list, replacing whatever the bridge holds: transitions are
 * cheap (a venue has tens of TVs, not thousands), and a frame that only
 * carried deltas would leave a reconnecting bridge asserting stale state
 * until the next change. Idempotent by design — re-sending the current list
 * must cause no visible flicker (the bridge compares before acting).
 */
exports.zTvDesiredState = zod_1.z.object({
    deviceId: zId,
    /** For the bridge's logs — "PS5-3", not an ip, when something fails. */
    deviceName: zod_1.z.string().max(120),
    /** The TV's static LAN address. */
    ip: zod_1.z.string().max(64),
    /**
     * HDMI input (1–4) the console occupies on this TV; the bridge sends the
     * matching Android `KEYCODE_TV_INPUT_HDMI_n` over ADB at reserve time.
     * Null = never switch inputs — rely on HDMI-CEC when the console wakes.
     */
    hdmiInput: zod_1.z.int().min(1).max(4).nullable(),
    /** `idle` loops the video; `console` stops it and switches to the HDMI input. */
    mode: zod_1.z.enum(['idle', 'console']),
    /**
     * The idle video, from the same §7 assignment PCs use. RELATIVE `path` on
     * purpose — the bridge resolves it against the server URL it was
     * provisioned with, so the frame works identically hosted and on-prem.
     * Null when nothing is assigned: idle then only stops the cast.
     */
    video: zod_1.z
        .object({
        assetId: zId,
        path: zod_1.z.string().min(1).max(300),
        checksum: zChecksum,
        mimeType: zod_1.z.string().max(120),
        sizeBytes: zod_1.z.int().min(0),
    })
        .nullable(),
});
exports.zTvStatePayload = zod_1.z.object({
    tvs: zod_1.z.array(exports.zTvDesiredState).max(200),
});
/** §7 — Android TV: start looping the idle video (`null` = use the cached default). */
exports.zTvIdleStartPayload = zod_1.z.object({
    deviceId: zId,
    assetId: zId.nullable(),
    loop: zod_1.z.boolean(),
    volumePercent: zod_1.z.int().min(0).max(100),
});
/** §7 — console reserved: get off the screen so HDMI-CEC can take it. */
exports.zTvIdleStopPayload = zod_1.z.object({
    deviceId: zId,
    reason: zod_1.z.enum(['session_start', 'manual', 'maintenance']),
});
/**
 * §7 — ask the bridge to sweep its own LAN for controllable Android TVs: a
 * TCP probe of every /24 it sits on for the ADB debug port, then one `adb
 * devices -l` for names and authorisation states. The reply is
 * `tv.scan_result`, matched by `requestId`; a bridge on a build older than
 * the scan simply never answers (agents subscribe only to the event names
 * they know), and the server times the request out.
 */
exports.zTvScanPayload = zod_1.z.object({
    requestId: zId,
});
/** §10 — a printer as configured in General Settings. */
exports.zPrinterTarget = zod_1.z.object({
    /** Configured name: SHISHA, BAR, CASH, KITCHEN. */
    name: zod_1.z.string().min(1).max(64),
    connection: zod_1.z.enum(['tcp', 'usb']),
    /** TCP only. */
    host: zod_1.z.string().max(255).nullable(),
    /** TCP only, normally 9100. */
    port: zod_1.z.int().min(1).max(65535).nullable(),
    /** USB only — exact Windows printer name on the main PC. */
    windowsPrinterName: zod_1.z.string().max(255).nullable(),
});
/**
 * §10 — a render-ready job. The server renders (ESC/POS bytes, base64) so
 * layout, Arabic/Kurdish shaping and totals live in one place; the bridge only
 * transports bytes to a printer and reports back.
 */
exports.zPrintJobPayload = zod_1.z.object({
    jobId: zId,
    printer: exports.zPrinterTarget,
    /** §6.1 "Copy Printing". */
    copies: zod_1.z.int().min(1).max(10),
    kind: zod_1.z.enum(['receipt', 'station_ticket', 'report', 'test']),
    format: zod_1.z.enum(['escpos', 'text']),
    /** base64 for `escpos`, UTF-8 text for `text`. */
    data: zod_1.z.string().min(1),
    meta: zod_1.z.object({
        billId: zId.nullable(),
        /** Station ticket destination: table/device label (§5.4). */
        destination: zod_1.z.string().max(120).nullable(),
        tabName: zod_1.z.string().max(120).nullable(),
        businessDate: zBusinessDate.nullable(),
        /** Grand total in millidinars, for the staff-app job list. */
        totalMd: zMillidinars.nullable(),
        printedBy: zod_1.z.string().max(120).nullable(),
    }),
});
/* -------------------------------------------------------------------------- */
/* Client -> server payloads                                                  */
/* -------------------------------------------------------------------------- */
exports.zAgentKind = zod_1.z.enum(['pc-agent', 'tv-agent', 'print-bridge']);
/** First frame after the socket opens; the server binds the socket to a device. */
exports.zAgentHelloPayload = zod_1.z.object({
    deviceTag: zDeviceTag,
    version: zSemver,
    agentKind: exports.zAgentKind,
    protocolVersion: zod_1.z.int().min(1),
    hostname: zod_1.z.string().max(255).nullable(),
    os: zod_1.z.string().max(120).nullable(),
    macAddress: zod_1.z.string().max(64).nullable(),
    /** Config version currently held; the server pushes `config.push` if stale. */
    configVersion: zod_1.z.int().min(0).nullable(),
    /** e.g. `['idle-video', 'hotkey', 'game-scan']` — lets the server skip pushes. */
    capabilities: zod_1.z.array(zod_1.z.string().max(64)),
});
/** §8 — liveness. Absence of heartbeats marks the device offline. */
exports.zAgentHeartbeatPayload = zod_1.z.object({
    sentAt: zInstant,
    uptimeSeconds: zod_1.z.int().min(0),
    /** Optional health telemetry; omit rather than send zeros. */
    cpuPercent: zod_1.z.number().min(0).max(100).nullable(),
    memoryPercent: zod_1.z.number().min(0).max(100).nullable(),
    freeDiskBytes: zod_1.z.int().min(0).nullable(),
});
exports.zAgentState = zod_1.z.enum([
    'locked_idle',
    'portal',
    'desktop',
    'maintenance',
    'updating',
    'error',
]);
/** What the device is actually doing — reconciles the staff app's live tiles. */
exports.zAgentStatusPayload = zod_1.z.object({
    state: exports.zAgentState,
    sessionId: zId.nullable(),
    /** Idle video currently cached/looping. */
    mediaAssetId: zId.nullable(),
    /** Game launched through the portal, if any. */
    gameId: zId.nullable(),
    foregroundApp: zod_1.z.string().max(255).nullable(),
    secondsRemaining: zod_1.z.int().nullable(),
    error: zod_1.z.string().max(500).nullable(),
});
/** §9 — what the customer did in the portal. Discriminated on `action`. */
exports.zPortalActionDetail = zod_1.z.discriminatedUnion('action', [
    zod_1.z.object({
        action: zod_1.z.literal('launch_game'),
        gameId: zId,
        /** §6.5 — sign in with their own account, or lease a venue account. */
        accountChoice: zod_1.z.enum(['own', 'venue']),
    }),
    zod_1.z.object({ action: zod_1.z.literal('launch_app'), appId: zId }),
    zod_1.z.object({
        action: zod_1.z.literal('place_order'),
        items: zod_1.z
            .array(zod_1.z.object({
            productId: zId,
            quantity: zod_1.z.int().min(1).max(99),
            note: zod_1.z.string().max(240).nullable(),
        }))
            .min(1),
        /** §6.1 "Portal Payment Methods", e.g. `visitor`, `balance`. */
        paymentMethod: zod_1.z.string().max(64),
    }),
    zod_1.z.object({ action: zod_1.z.literal('redeem_prize'), prizeId: zId }),
    zod_1.z.object({ action: zod_1.z.literal('request_extension'), minutes: zod_1.z.int().min(1).max(1440) }),
    zod_1.z.object({ action: zod_1.z.literal('high_five'), socialEventId: zId }),
    zod_1.z.object({ action: zod_1.z.literal('logout') }),
]);
exports.zPortalActionPayload = zod_1.z.object({
    deviceId: zId,
    sessionId: zId.nullable(),
    /** Portal account performing the action; `null` for a visitor session. */
    customerId: zId.nullable(),
    detail: exports.zPortalActionDetail,
});
/**
 * §7 — the bridge reporting one TV action's outcome. Telemetry, not control
 * flow: the server logs failures so a TV that stopped answering is visible in
 * the server logs, and nothing waits on this frame.
 */
exports.zTvResultPayload = zod_1.z.object({
    deviceId: zId,
    ip: zod_1.z.string().max(64),
    mode: zod_1.z.enum(['idle', 'console']),
    /** Which leg acted: the Cast video or the ADB input switch. */
    transport: zod_1.z.enum(['cast', 'adb']),
    ok: zod_1.z.boolean(),
    completedAt: zInstant,
    error: zod_1.z.string().max(500).nullable(),
});
/** §7 — one LAN host that answered the ADB probe during a `tv.scan`. */
exports.zTvScanCandidate = zod_1.z.object({
    ip: zod_1.z.string().max(64),
    /**
     * `authorized` = the bridge can drive it today; `unauthorized` = the TV is
     * waiting for its "Allow USB debugging?" dialog to be accepted; `offline` =
     * adb saw it but the link is not usable yet; `unknown` = the port answered
     * but adb said nothing definite.
     */
    adbState: zod_1.z.enum(['authorized', 'unauthorized', 'offline', 'unknown']),
    /** From `adb devices -l` (`model:`), when the TV is authorised. */
    model: zod_1.z.string().max(120).nullable(),
});
exports.zTvScanResultPayload = zod_1.z.object({
    requestId: zId,
    tvs: zod_1.z.array(exports.zTvScanCandidate).max(200),
    /** Hosts probed — lets the UI say "scanned 254 addresses" honestly. */
    scannedCount: zod_1.z.int().min(0),
});
/** §10 — outcome of a print job; failures surface as staff-app alerts. */
exports.zPrintResultPayload = zod_1.z.object({
    jobId: zId,
    printerName: zod_1.z.string().min(1).max(64),
    ok: zod_1.z.boolean(),
    /** 1-based; the bridge retries and reports each attempt. */
    attempt: zod_1.z.int().min(1),
    completedAt: zInstant,
    error: zod_1.z
        .object({
        code: zod_1.z.enum(['not_found', 'offline', 'out_of_paper', 'timeout', 'render', 'unknown']),
        message: zod_1.z.string().max(500),
    })
        .nullable(),
});
/* -------------------------------------------------------------------------- */
/* Event names                                                                */
/* -------------------------------------------------------------------------- */
/** Server -> client event names. Import these instead of retyping literals. */
exports.SERVER_EVENTS = {
    DEVICE_UNLOCK: 'device.unlock',
    DEVICE_LOCK: 'device.lock',
    SESSION_PAUSE: 'session.pause',
    SESSION_RESUME: 'session.resume',
    APP_CHECK_UPDATE: 'app.check_update',
    PORTAL_RELOAD: 'portal.reload',
    DEVICE_WARN: 'device.warn',
    DEVICE_LAUNCH: 'device.launch',
    DEVICE_POWER: 'device.power',
    MEDIA_PUSH: 'media.push',
    AGENT_UPDATE: 'agent.update',
    CONFIG_PUSH: 'config.push',
    TV_STATE: 'tv.state',
    TV_IDLE_START: 'tv.idle_start',
    TV_IDLE_STOP: 'tv.idle_stop',
    TV_SCAN: 'tv.scan',
    PRINT_JOB: 'print.job',
};
/** Client -> server event names. */
exports.CLIENT_EVENTS = {
    AGENT_HELLO: 'agent.hello',
    AGENT_HEARTBEAT: 'agent.heartbeat',
    AGENT_STATUS: 'agent.status',
    PORTAL_ACTION: 'portal.action',
    PRINT_RESULT: 'print.result',
    TV_RESULT: 'tv.result',
    TV_SCAN_RESULT: 'tv.scan_result',
};
exports.EVENTS = { ...exports.SERVER_EVENTS, ...exports.CLIENT_EVENTS };
/* -------------------------------------------------------------------------- */
/* Message schemas & discriminated unions                                     */
/* -------------------------------------------------------------------------- */
exports.zDeviceUnlockMessage = message(exports.SERVER_EVENTS.DEVICE_UNLOCK, exports.zDeviceUnlockPayload);
exports.zDeviceLockMessage = message(exports.SERVER_EVENTS.DEVICE_LOCK, exports.zDeviceLockPayload);
exports.zSessionPauseMessage = message(exports.SERVER_EVENTS.SESSION_PAUSE, exports.zSessionPausePayload);
exports.zSessionResumeMessage = message(exports.SERVER_EVENTS.SESSION_RESUME, exports.zSessionResumePayload);
exports.zAppCheckUpdateMessage = message(exports.SERVER_EVENTS.APP_CHECK_UPDATE, exports.zAppCheckUpdatePayload);
exports.zPortalReloadMessage = message(exports.SERVER_EVENTS.PORTAL_RELOAD, exports.zPortalReloadPayload);
exports.zDevicePowerMessage = message(exports.SERVER_EVENTS.DEVICE_POWER, exports.zDevicePowerPayload);
exports.zDeviceWarnMessage = message(exports.SERVER_EVENTS.DEVICE_WARN, exports.zDeviceWarnPayload);
exports.zDeviceLaunchMessage = message(exports.SERVER_EVENTS.DEVICE_LAUNCH, exports.zDeviceLaunchPayload);
exports.zMediaPushMessage = message(exports.SERVER_EVENTS.MEDIA_PUSH, exports.zMediaPushPayload);
exports.zAgentUpdateMessage = message(exports.SERVER_EVENTS.AGENT_UPDATE, exports.zAgentUpdatePayload);
exports.zConfigPushMessage = message(exports.SERVER_EVENTS.CONFIG_PUSH, exports.zConfigPushPayload);
exports.zTvStateMessage = message(exports.SERVER_EVENTS.TV_STATE, exports.zTvStatePayload);
exports.zTvIdleStartMessage = message(exports.SERVER_EVENTS.TV_IDLE_START, exports.zTvIdleStartPayload);
exports.zTvIdleStopMessage = message(exports.SERVER_EVENTS.TV_IDLE_STOP, exports.zTvIdleStopPayload);
exports.zTvScanMessage = message(exports.SERVER_EVENTS.TV_SCAN, exports.zTvScanPayload);
exports.zPrintJobMessage = message(exports.SERVER_EVENTS.PRINT_JOB, exports.zPrintJobPayload);
exports.zAgentHelloMessage = message(exports.CLIENT_EVENTS.AGENT_HELLO, exports.zAgentHelloPayload);
exports.zAgentHeartbeatMessage = message(exports.CLIENT_EVENTS.AGENT_HEARTBEAT, exports.zAgentHeartbeatPayload);
exports.zAgentStatusMessage = message(exports.CLIENT_EVENTS.AGENT_STATUS, exports.zAgentStatusPayload);
exports.zPortalActionMessage = message(exports.CLIENT_EVENTS.PORTAL_ACTION, exports.zPortalActionPayload);
exports.zPrintResultMessage = message(exports.CLIENT_EVENTS.PRINT_RESULT, exports.zPrintResultPayload);
exports.zTvResultMessage = message(exports.CLIENT_EVENTS.TV_RESULT, exports.zTvResultPayload);
exports.zTvScanResultMessage = message(exports.CLIENT_EVENTS.TV_SCAN_RESULT, exports.zTvScanResultPayload);
/** Everything the server may push down an agent socket. */
exports.zServerMessage = zod_1.z.discriminatedUnion('event', [
    exports.zDeviceUnlockMessage,
    exports.zDeviceLockMessage,
    exports.zSessionPauseMessage,
    exports.zSessionResumeMessage,
    exports.zAppCheckUpdateMessage,
    exports.zPortalReloadMessage,
    exports.zDevicePowerMessage,
    exports.zDeviceWarnMessage,
    exports.zDeviceLaunchMessage,
    exports.zMediaPushMessage,
    exports.zAgentUpdateMessage,
    exports.zConfigPushMessage,
    exports.zTvStateMessage,
    exports.zTvIdleStartMessage,
    exports.zTvIdleStopMessage,
    exports.zTvScanMessage,
    exports.zPrintJobMessage,
]);
/** Everything an agent or the print bridge may send up. */
exports.zClientMessage = zod_1.z.discriminatedUnion('event', [
    exports.zAgentHelloMessage,
    exports.zAgentHeartbeatMessage,
    exports.zAgentStatusMessage,
    exports.zPortalActionMessage,
    exports.zPrintResultMessage,
    exports.zTvResultMessage,
    exports.zTvScanResultMessage,
]);
exports.zAnyMessage = zod_1.z.union([exports.zServerMessage, exports.zClientMessage]);
/** Per-event schema lookup, e.g. for a socket router keyed on the event name. */
exports.SERVER_MESSAGE_SCHEMAS = {
    'device.unlock': exports.zDeviceUnlockMessage,
    'device.lock': exports.zDeviceLockMessage,
    'session.pause': exports.zSessionPauseMessage,
    'session.resume': exports.zSessionResumeMessage,
    'app.check_update': exports.zAppCheckUpdateMessage,
    'portal.reload': exports.zPortalReloadMessage,
    'device.power': exports.zDevicePowerMessage,
    'device.warn': exports.zDeviceWarnMessage,
    'device.launch': exports.zDeviceLaunchMessage,
    'media.push': exports.zMediaPushMessage,
    'agent.update': exports.zAgentUpdateMessage,
    'config.push': exports.zConfigPushMessage,
    'tv.state': exports.zTvStateMessage,
    'tv.idle_start': exports.zTvIdleStartMessage,
    'tv.idle_stop': exports.zTvIdleStopMessage,
    'tv.scan': exports.zTvScanMessage,
    'print.job': exports.zPrintJobMessage,
};
exports.CLIENT_MESSAGE_SCHEMAS = {
    'agent.hello': exports.zAgentHelloMessage,
    'agent.heartbeat': exports.zAgentHeartbeatMessage,
    'agent.status': exports.zAgentStatusMessage,
    'portal.action': exports.zPortalActionMessage,
    'print.result': exports.zPrintResultMessage,
    'tv.result': exports.zTvResultMessage,
    'tv.scan_result': exports.zTvScanResultMessage,
};
function newId() {
    // Web Crypto is present in Node 19+ and every supported browser; the fallback
    // keeps this module usable in exotic embedders without a DOM lib dependency.
    const cryptoRef = globalThis.crypto;
    if (typeof cryptoRef?.randomUUID === 'function')
        return cryptoRef.randomUUID();
    return `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
/** Build a server->client message with a fresh envelope. Payload is type-checked. */
function createServerMessage(event, payload, options = {}) {
    return {
        id: options.id ?? newId(),
        ts: options.ts ?? new Date().toISOString(),
        ...(options.correlationId === undefined ? {} : { correlationId: options.correlationId }),
        event,
        payload,
    };
}
/** Build a client->server message with a fresh envelope. */
function createClientMessage(event, payload, options = {}) {
    return {
        id: options.id ?? newId(),
        ts: options.ts ?? new Date().toISOString(),
        ...(options.correlationId === undefined ? {} : { correlationId: options.correlationId }),
        event,
        payload,
    };
}
/** Validate an inbound frame on an agent/bridge. Throws `ZodError` when invalid. */
function parseServerMessage(raw) {
    return exports.zServerMessage.parse(raw);
}
/** Validate an inbound frame on the API. Throws `ZodError` when invalid. */
function parseClientMessage(raw) {
    return exports.zClientMessage.parse(raw);
}
function safeParseServerMessage(raw) {
    return exports.zServerMessage.safeParse(raw);
}
function safeParseClientMessage(raw) {
    return exports.zClientMessage.safeParse(raw);
}
function isServerEventName(value) {
    return typeof value === 'string' && value in exports.SERVER_MESSAGE_SCHEMAS;
}
function isClientEventName(value) {
    return typeof value === 'string' && value in exports.CLIENT_MESSAGE_SCHEMAS;
}
//# sourceMappingURL=events.js.map